import React, { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import { useSearchParams, Link } from "react-router-dom";
import PageLayout, {
  PageSection,
} from "../../components/navigation/PageLayout";
import CalculationSummary from "./components/CalculationSummary";
import EmployeeDistributionList from "./components/EmployeeDistributionList";
import ComplianceSidebar from "./components/ComplianceSidebar";
import Icon from "../../components/AppIcon";
import { useEmployees } from "../../hooks/useEmployees";
import { useTroncRecords } from "../../hooks/useTroncRecords";
import {
  useDistributionRules,
  useDistributionRuleMutations,
} from "../../hooks/useDistributionRules";
import { useLocationContext } from "../../contexts/LocationContext";
import { useTenantContext } from "../../contexts/TenantContext";
import { validateDistributionTotal } from "../../utils/distributionCalculator";

// Helper to format date for display
const formatDate = (date) => {
  if (!date) return "";
  const d = new Date(date);
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

// Helper to format date as ISO string (YYYY-MM-DD)
const toISODate = (date) => {
  if (!date) return null;
  const d = new Date(date);
  return d.toISOString().split("T")[0];
};

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(amount || 0);
};

// Get distribution type info
const getDistributionTypeInfo = (type) => {
  // Matches backend enum: Equal=0, RoleScore=1, ByDepartment=2, WeightedHours=3
  const typeMap = {
    Equal: {
      name: "Equal Split",
      icon: "Users",
      color: "text-blue-600",
      bgColor: "bg-blue-100",
    },
    RoleScore: {
      name: "By Job Role",
      icon: "PieChart",
      color: "text-purple-600",
      bgColor: "bg-purple-100",
    },
    ByDepartment: {
      name: "By Department",
      icon: "Building2",
      color: "text-emerald-600",
      bgColor: "bg-emerald-100",
    },
    WeightedHours: {
      name: "Weighted Hours",
      icon: "Clock",
      color: "text-cyan-600",
      bgColor: "bg-cyan-100",
    },
    // Legacy string aliases
    ByJobRole: {
      name: "By Job Role",
      icon: "PieChart",
      color: "text-purple-600",
      bgColor: "bg-purple-100",
    },
    ByJobRoleScore: {
      name: "By Job Role",
      icon: "PieChart",
      color: "text-purple-600",
      bgColor: "bg-purple-100",
    },
    ByHoursAndJobRole: {
      name: "Weighted Hours",
      icon: "Clock",
      color: "text-cyan-600",
      bgColor: "bg-cyan-100",
    },
    // Numeric keys matching backend enum
    0: {
      name: "Equal Split",
      icon: "Users",
      color: "text-blue-600",
      bgColor: "bg-blue-100",
    },
    1: {
      name: "By Job Role",
      icon: "PieChart",
      color: "text-purple-600",
      bgColor: "bg-purple-100",
    },
    2: {
      name: "By Department",
      icon: "Building2",
      color: "text-emerald-600",
      bgColor: "bg-emerald-100",
    },
    3: {
      name: "Weighted Hours",
      icon: "Clock",
      color: "text-cyan-600",
      bgColor: "bg-cyan-100",
    },
  };
  return (
    typeMap[type] || {
      name: "Custom",
      icon: "Settings",
      color: "text-gray-600",
      bgColor: "bg-gray-100",
    }
  );
};

const TroncDistribution = () => {
  const { currentLocation, hasLocations } = useLocationContext();
  const { selectedTenant, needsTenantSelection, isGlobalAdmin } =
    useTenantContext();
  const [searchParams] = useSearchParams();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedRuleId, setSelectedRuleId] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [employeeAmounts, setEmployeeAmounts] = useState({});
  const [overrideNotes, setOverrideNotes] = useState({}); // Track override notes
  const [originalAmounts, setOriginalAmounts] = useState({}); // Track original calculated amounts
  const [calculatedDistribution, setCalculatedDistribution] = useState([]);
  const [notification, setNotification] = useState(null);
  const [activeRule, setActiveRule] = useState(null);
  const [loadingRule, setLoadingRule] = useState(true);
  const [showApprovalModal, setShowApprovalModal] = useState(false);

  // Only fetch data when tenant is selected (or for non-global admins)
  const shouldFetchData = !isGlobalAdmin || selectedTenant;

  // Fetch real data
  const { employees: apiEmployees, loading: employeesLoading } = useEmployees({
    autoFetch: shouldFetchData,
    troncEligibleOnly: true,
  });
  const {
    records: troncRecords,
    loading: troncLoading,
    refetch: refetchTroncRecords,
  } = useTroncRecords({
    autoFetch: shouldFetchData,
  });
  const {
    rules,
    loading: rulesLoading,
    refetch: refetchRules,
  } = useDistributionRules({
    activeOnly: true,
    autoFetch: shouldFetchData,
  });
  const { previewDistribution, loading: previewLoading } =
    useDistributionRuleMutations();

  const loading =
    employeesLoading || troncLoading || rulesLoading || loadingRule;

  // Filter employees by current location
  const locationFilteredEmployees = currentLocation
    ? (apiEmployees || []).filter(
        (emp) => emp.locationId === currentLocation.id,
      )
    : apiEmployees || [];

  // Auto-load active rule when location or rules change
  useEffect(() => {
    const loadActiveRule = async () => {
      if (!rules || rules.length === 0) {
        setActiveRule(null);
        setLoadingRule(false);
        return;
      }

      setLoadingRule(true);
      try {
        // Find active rules: prefer location-specific, fall back to tenant-wide (no location)
        const activeRules = rules.filter((r) => r.isActive);
        let matchedRule = null;

        if (currentLocation) {
          // First try location-specific rules
          matchedRule = activeRules.find(
            (r) =>
              r.location === currentLocation.name ||
              r.location === currentLocation.id ||
              r.locationId === currentLocation.id,
          );
        }

        // Fall back to any active rule with no location restriction
        if (!matchedRule) {
          matchedRule =
            activeRules.find((r) => !r.location && !r.locationId) ||
            activeRules[0] ||
            null;
        }

        if (matchedRule) {
          setActiveRule(matchedRule);
          setSelectedRuleId(matchedRule.id);
        } else {
          setActiveRule(null);
          setSelectedRuleId(null);
          showNotification(
            "No active distribution rule found. Please configure one in Settings.",
            "warning",
          );
        }
      } catch (err) {
        console.error("Error loading active rule:", err);
        showNotification("Failed to load distribution rule", "error");
      } finally {
        setLoadingRule(false);
      }
    };

    loadActiveRule();
  }, [currentLocation?.id, rules]);

  // Get TRONC records filtered by location
  const locationRecords = useMemo(() => {
    if (!troncRecords) return [];
    const filtered = currentLocation
      ? troncRecords.filter((r) => r.locationId === currentLocation.id)
      : troncRecords;
    // Exclude records with zero or negative amounts - only positive values go into distribution
    return filtered.filter((r) => (r.amount || 0) > 0);
  }, [troncRecords, currentLocation?.id]);

  // Get last processed date and available dates for processing
  const { lastProcessedDate, availableDates, nextDateToProcess } =
    useMemo(() => {
      if (!locationRecords.length) {
        return {
          lastProcessedDate: null,
          availableDates: [],
          nextDateToProcess: null,
        };
      }

      const distributedRecords = locationRecords.filter((r) => r.isDistributed);
      const pendingRecords = locationRecords.filter((r) => !r.isDistributed);

      let lastProcessed = null;
      if (distributedRecords.length > 0) {
        const sortedDistributed = [...distributedRecords].sort(
          (a, b) =>
            new Date(b.periodStart || b.date) -
            new Date(a.periodStart || a.date),
        );
        lastProcessed = toISODate(
          sortedDistributed[0].periodStart || sortedDistributed[0].date,
        );
      }

      // All unique dates from unprocessed (pending) records, oldest first
      const available = [
        ...new Set(
          pendingRecords.map((r) => toISODate(r.periodStart || r.date)),
        ),
      ]
        .filter((d) => d)
        .sort();

      // Default to the oldest unprocessed date so nothing gets skipped
      const nextDate = available.length > 0 ? available[0] : null;

      return {
        lastProcessedDate: lastProcessed,
        availableDates: available,
        nextDateToProcess: nextDate,
      };
    }, [locationRecords]);

  // Auto-select next date to process or date from URL
  useEffect(() => {
    // Check if date is in URL parameters
    const dateFromUrl = searchParams.get("date");
    const recordIdFromUrl = searchParams.get("recordId");

    if (dateFromUrl) {
      // Pre-select the date from URL
      setSelectedDate(dateFromUrl);
      console.log("Pre-selecting date from URL:", dateFromUrl);
    } else if (nextDateToProcess && !selectedDate) {
      // Otherwise auto-select next date
      setSelectedDate(nextDateToProcess);
    }
  }, [nextDateToProcess, searchParams]);

  // Reset when location changes
  useEffect(() => {
    setSelectedDate(null);
    setEmployeeAmounts({});
    setSelectedRuleId(null);
  }, [currentLocation?.id]);

  // Get TRONC amount for selected date
  const selectedDateRecords = useMemo(() => {
    if (!selectedDate || !locationRecords.length) return [];
    return locationRecords.filter((r) => {
      const recordDate = toISODate(r.periodStart || r.date);
      return recordDate === selectedDate && !r.isDistributed;
    });
  }, [locationRecords, selectedDate]);

  // Calculate total TRONC amount from selected date records
  const totalTroncAmount = useMemo(() => {
    return selectedDateRecords.reduce((sum, r) => sum + (r.amount || 0), 0);
  }, [selectedDateRecords]);

  // Transform employees for display - build a lookup from API employees
  const employeeLookup = useMemo(() => {
    const lookup = {};
    (locationFilteredEmployees || []).forEach((emp) => {
      lookup[emp.id] = emp;
    });
    return lookup;
  }, [locationFilteredEmployees]);

  // Eligible employees: start with locally-filtered active employees,
  // but don't filter by isEligibleForTronc or jobRoleId - the backend handles eligibility checks.
  // This ensures employees returned by the preview API can always be displayed.
  const eligibleEmployees = useMemo(() => {
    // If we have preview results, use those as the source of truth
    if (calculatedDistribution.length > 0) {
      return calculatedDistribution.map((d) => {
        const emp = employeeLookup[d.employeeId];
        return {
          id: d.employeeId,
          name:
            d.employeeName ||
            emp?.fullName ||
            `${emp?.firstName || ""} ${emp?.lastName || ""}`.trim() ||
            "Unknown",
          role:
            d.jobRoleName ||
            emp?.jobTitle ||
            emp?.primaryJobRoleName ||
            "Unassigned",
          jobRoleId: emp?.primaryJobRoleId || emp?.jobRoleId,
          primaryJobRoleId: emp?.primaryJobRoleId,
          department: emp?.department,
          avatar:
            emp?.photoUrl ||
            `https://ui-avatars.com/api/?name=${encodeURIComponent(d.employeeName || "U")}&background=1a365d&color=fff`,
          hoursWorked: d.hoursWorked || emp?.contractedHoursPerWeek || 0,
          troncScore: emp?.troncScore || 1,
          calculatedAmount: d.amount || 0,
        };
      });
    }

    // Fallback: build from local employee list (before preview runs)
    return locationFilteredEmployees
      .filter((emp) => emp.isActive)
      .map((emp) => ({
        id: emp.id,
        name:
          emp.fullName || `${emp.firstName || ""} ${emp.lastName || ""}`.trim(),
        role: emp.jobTitle || emp.primaryJobRoleName || "Unassigned",
        jobRoleId: emp.primaryJobRoleId || emp.jobRoleId,
        primaryJobRoleId: emp.primaryJobRoleId,
        department: emp.department,
        avatar:
          emp.photoUrl ||
          `https://ui-avatars.com/api/?name=${encodeURIComponent(emp.firstName || "")}+${encodeURIComponent(emp.lastName || "")}&background=1a365d&color=fff`,
        hoursWorked: emp.contractedHoursPerWeek || 0,
        troncScore: emp.troncScore || 1,
        calculatedAmount: 0,
      }));
  }, [locationFilteredEmployees, calculatedDistribution, employeeLookup]);

  // Calculate distribution when rule or amount changes
  // We track the count of location-filtered employees (not eligibleEmployees which depends on calculatedDistribution)
  const locationEmployeeCount = locationFilteredEmployees.filter(
    (e) => e.isActive,
  ).length;

  useEffect(() => {
    const runCalculation = async () => {
      if (
        !activeRule ||
        totalTroncAmount === 0 ||
        locationEmployeeCount === 0
      ) {
        setCalculatedDistribution([]);
        setEmployeeAmounts({});
        setOriginalAmounts({});
        return;
      }

      try {
        // Pass selectedDate as periodStart/periodEnd so the backend uses clock records for the correct date
        const periodStart = selectedDate ? new Date(selectedDate).toISOString() : null;
        const periodEnd = selectedDate ? new Date(selectedDate + 'T23:59:59').toISOString() : null;
        const result = await previewDistribution(
          activeRule.id,
          totalTroncAmount,
          currentLocation?.id || null,
          periodStart,
          periodEnd,
        );

        if (result.success && result.data?.distributions) {
          setCalculatedDistribution(result.data.distributions);

          const amounts = {};
          result.data.distributions.forEach((d) => {
            amounts[d.employeeId] = d.amount;
          });
          setEmployeeAmounts(amounts);
          setOriginalAmounts(amounts); // Store original amounts
        } else {
          showNotification(
            "Failed to calculate distribution: " +
              (result.message || "Unknown error"),
            "error",
          );
          calculateLocalDistribution();
        }
      } catch (err) {
        console.error("Preview failed:", err);
        showNotification(
          "Distribution preview failed. Using local calculation.",
          "warning",
        );
        calculateLocalDistribution();
      }
    };

    const calculateLocalDistribution = () => {
      // Local fallback calculation matching backend enum: Equal=0, RoleScore=1, ByDepartment=2, WeightedHours=3
      const amounts = {};
      // Use locationFilteredEmployees for local fallback since we don't have preview results
      const localEligible = locationFilteredEmployees.filter(
        (emp) => emp.isActive,
      );
      const ruleType = activeRule?.type;

      if (ruleType === "Equal" || ruleType === 0) {
        // Equal split
        const equalAmount =
          Math.round((totalTroncAmount / localEligible.length) * 100) / 100;
        let remaining = totalTroncAmount;
        localEligible.forEach((emp, index) => {
          if (index === localEligible.length - 1) {
            amounts[emp.id] = remaining;
          } else {
            amounts[emp.id] = equalAmount;
            remaining -= equalAmount;
          }
        });
      } else if (ruleType === "RoleScore" || ruleType === "ByJobRole" || ruleType === "ByJobRoleScore" || ruleType === 1) {
        // RoleScore: split by job role percentage allocations
        if (!activeRule.allocations || activeRule.allocations.length === 0) {
          showNotification("Rule has no allocations defined", "error");
          return;
        }
        const employeesByRole = {};
        localEligible.forEach((emp) => {
          const roleId = emp.primaryJobRoleId || emp.jobRoleId;
          if (!roleId) return;
          if (!employeesByRole[roleId]) employeesByRole[roleId] = [];
          employeesByRole[roleId].push(emp);
        });
        const allocs = activeRule.allocations;
        const totalRoleMultiplier = allocs.reduce((sum, a) => sum + (a.scoreMultiplier || 1), 0) || 1;
        activeRule.allocations.forEach((allocation) => {
          const roleEmployees = employeesByRole[allocation.jobRoleId] || [];
          if (roleEmployees.length === 0) return;
          const roleAmount =
            Math.round(((totalTroncAmount * (allocation.scoreMultiplier || 1)) / totalRoleMultiplier) * 100) / 100;
          const amountPerEmployee =
            Math.round((roleAmount / roleEmployees.length) * 100) / 100;
          let roleRemaining = roleAmount;
          roleEmployees.forEach((emp, index) => {
            if (index === roleEmployees.length - 1) {
              amounts[emp.id] = roleRemaining;
            } else {
              amounts[emp.id] = amountPerEmployee;
              roleRemaining -= amountPerEmployee;
            }
          });
        });
      } else if (ruleType === "ByDepartment" || ruleType === 2) {
        // ByDepartment: split by department percentage allocations
        if (!activeRule.allocations || activeRule.allocations.length === 0) {
          showNotification("Rule has no allocations defined", "error");
          return;
        }
        const employeesByDept = {};
        localEligible.forEach((emp) => {
          const dept = emp.department || "FOH";
          if (!employeesByDept[dept]) employeesByDept[dept] = [];
          employeesByDept[dept].push(emp);
        });
        const deptAllocs = activeRule.allocations;
        const totalDeptMultiplier = deptAllocs.reduce((sum, a) => sum + (a.scoreMultiplier || 1), 0) || 1;
        activeRule.allocations.forEach((allocation) => {
          if (!allocation.department) return;
          const deptEmployees = employeesByDept[allocation.department] || [];
          if (deptEmployees.length === 0) return;
          const deptAmount =
            Math.round(((totalTroncAmount * (allocation.scoreMultiplier || 1)) / totalDeptMultiplier) * 100) / 100;
          const amountPerEmployee =
            Math.round((deptAmount / deptEmployees.length) * 100) / 100;
          let deptRemaining = deptAmount;
          deptEmployees.forEach((emp, index) => {
            if (index === deptEmployees.length - 1) {
              amounts[emp.id] = deptRemaining;
            } else {
              amounts[emp.id] = amountPerEmployee;
              deptRemaining -= amountPerEmployee;
            }
          });
        });
      } else if (ruleType === "WeightedHours" || ruleType === 3) {
        // WeightedHours: Individual points = hours × multiplier from rule allocations
        // The primary backend path handles this correctly; this is an emergency fallback
        const roleMultiplierMap = {};
        (activeRule.allocations || []).filter(a => a.jobRoleId && (a.scoreMultiplier || 0) > 0).forEach(a => {
          roleMultiplierMap[a.jobRoleId] = a.scoreMultiplier;
        });
        let totalPoints = 0;
        const employeePoints = {};
        localEligible.forEach((emp) => {
          const hours = emp.contractedHoursPerWeek || 0;
          const roleId = emp.primaryJobRoleId || emp.jobRoleId;
          const multiplier = (roleId && roleMultiplierMap[roleId]) ? roleMultiplierMap[roleId] : 1.0;
          const points = hours * multiplier;
          employeePoints[emp.id] = points;
          totalPoints += points;
        });
        if (totalPoints === 0) {
          // Fall back to equal if no hours data
          const equalAmount =
            Math.round((totalTroncAmount / localEligible.length) * 100) / 100;
          let remaining = totalTroncAmount;
          localEligible.forEach((emp, index) => {
            if (index === localEligible.length - 1) {
              amounts[emp.id] = remaining;
            } else {
              amounts[emp.id] = equalAmount;
              remaining -= equalAmount;
            }
          });
        } else {
          let distributed = 0;
          localEligible.forEach((emp, index) => {
            const points = employeePoints[emp.id] || 0;
            let amount;
            if (index === localEligible.length - 1) {
              amount = totalTroncAmount - distributed;
            } else {
              amount = Math.round((points / totalPoints) * totalTroncAmount * 100) / 100;
              distributed += amount;
            }
            amounts[emp.id] = amount;
          });
        }
      }

      setEmployeeAmounts(amounts);
      setOriginalAmounts(amounts); // Store original amounts
    };

    if (activeRule && totalTroncAmount > 0 && locationEmployeeCount > 0) {
      runCalculation();
    }
  }, [
    activeRule?.id,
    totalTroncAmount,
    locationEmployeeCount,
    currentLocation?.id,
  ]);

  // Validate distribution
  const distributionValidation = useMemo(() => {
    if (Object.keys(employeeAmounts).length === 0) {
      return { isValid: false, difference: totalTroncAmount };
    }
    return validateDistributionTotal(
      totalTroncAmount,
      Object.entries(employeeAmounts).map(([id, amount]) => ({ amount })),
    );
  }, [employeeAmounts, totalTroncAmount]);

  // Calculate strategy breakdown with real figures
  const strategyBreakdown = useMemo(() => {
    if (
      !activeRule ||
      totalTroncAmount === 0 ||
      eligibleEmployees.length === 0
    ) {
      return null;
    }

    const ruleType = activeRule.type;

    // Equal Split
    if (ruleType === "Equal" || ruleType === 0) {
      const perEmployee = totalTroncAmount / eligibleEmployees.length;
      return {
        type: "equal",
        description: "Total TRONC divided equally among all eligible employees",
        formula: `{totalTroncAmount} ÷ ${eligibleEmployees.length} employees`,
        result: formatCurrency(perEmployee),
        items: [
          {
            label: "Per Employee",
            value: formatCurrency(perEmployee),
            count: eligibleEmployees.length,
            percentage: 100,
          },
        ],
      };
    }

    // By Job Role (RoleScore)
    if (ruleType === "RoleScore" || ruleType === "ByJobRole" || ruleType === "ByJobRoleScore" || ruleType === 1) {
      const totalRoleMult = (activeRule.allocations || []).reduce((sum, a) => sum + (a.scoreMultiplier || 1), 0) || 1;
      const items = (activeRule.allocations || []).map((allocation) => {
        const roleEmployees = eligibleEmployees.filter(
          (e) =>
            e.jobRoleId === allocation.jobRoleId ||
            e.primaryJobRoleId === allocation.jobRoleId,
        );
        const mult = allocation.scoreMultiplier || 1;
        const roleAmount = totalTroncAmount * (mult / totalRoleMult);
        const perEmployee =
          roleEmployees.length > 0 ? roleAmount / roleEmployees.length : 0;

        return {
          label: allocation.jobRoleName || "Unknown Role",
          percentage: mult / totalRoleMult * 100,
          value: formatCurrency(roleAmount),
          count: roleEmployees.length,
          perEmployee: formatCurrency(perEmployee),
        };
      });

      return {
        type: "byJobRole",
        description: "TRONC split by configured score multiplier per job role",
        formula: "(Role Multiplier ÷ Total Multipliers) × Total TRONC ÷ Employees in Role",
        items,
      };
    }

    // By Department
    if (ruleType === "ByDepartment" || ruleType === 2) {
      const totalDeptMult = (activeRule.allocations || []).reduce((sum, a) => sum + (a.scoreMultiplier || 1), 0) || 1;
      const items = (activeRule.allocations || []).map((allocation) => {
        const deptEmployees = eligibleEmployees.filter(
          (e) => e.department === allocation.department,
        );
        const mult = allocation.scoreMultiplier || 1;
        const deptAmount = totalTroncAmount * (mult / totalDeptMult);
        const perEmployee =
          deptEmployees.length > 0 ? deptAmount / deptEmployees.length : 0;

        return {
          label: allocation.department || "Unknown Department",
          percentage: mult / totalDeptMult * 100,
          value: formatCurrency(deptAmount),
          count: deptEmployees.length,
          perEmployee: formatCurrency(perEmployee),
        };
      });

      return {
        type: "byDepartment",
        description: "TRONC split by configured score multiplier per department",
        formula: "(Dept Multiplier ÷ Total Multipliers) × Total TRONC ÷ Employees in Dept",
        items,
      };
    }

    // Weighted Hours
    if (ruleType === "WeightedHours" || ruleType === 3) {
      const employeeWeights = eligibleEmployees.map((emp) => ({
        ...emp,
        weight: (emp.hoursWorked || 0) * (emp.troncScore || 1),
      }));
      const totalWeight = employeeWeights.reduce(
        (sum, emp) => sum + emp.weight,
        0,
      );

      // Group by role
      const roleGroups = {};
      employeeWeights.forEach((emp) => {
        const roleName = emp.role || "Unassigned";
        if (!roleGroups[roleName]) {
          roleGroups[roleName] = { employees: [], totalWeight: 0 };
        }
        roleGroups[roleName].employees.push(emp);
        roleGroups[roleName].totalWeight += emp.weight;
      });

      const items = Object.entries(roleGroups).map(([roleName, data]) => {
        const rolePercentage = totalWeight > 0 ? (data.totalWeight / totalWeight) * 100 : 0;
        const roleAmount = totalTroncAmount * (rolePercentage / 100);
        const perEmployee = data.employees.length > 0 ? roleAmount / data.employees.length : 0;

        return {
          label: roleName,
          percentage: Math.round(rolePercentage * 10) / 10,
          value: formatCurrency(roleAmount),
          count: data.employees.length,
          perEmployee: formatCurrency(perEmployee),
          weight: Math.round(data.totalWeight),
        };
      });

      return {
        type: "byWeightedHours",
        description: "Hours worked × role multiplier determines individual share",
        formula: "(Hours × Multiplier) ÷ Total Points × Total TRONC",
        totalWeight: Math.round(totalWeight),
        items: items.sort((a, b) => b.percentage - a.percentage),
      };
    }

    return null;
  }, [activeRule, totalTroncAmount, eligibleEmployees, employeeAmounts]);

  const breadcrumbs = [
    { label: "Dashboard", path: "/dashboard-overview" },
    { label: "TRONC Distribution", path: "/tronc-distribution" },
  ];

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setEmployeeAmounts({});
  };

  const handleAmountChange = (employeeId, value) => {
    const roundedValue = Math.round(parseFloat(value) * 100) / 100;
    setEmployeeAmounts((prev) => ({
      ...prev,
      [employeeId]: roundedValue || 0,
    }));
  };

  const handleOverrideNotesChange = (notes) => {
    setOverrideNotes(notes);
  };

  // Check if there are any modified amounts that need notes
  const getEmployeesNeedingNotes = () => {
    const needingNotes = [];
    Object.keys(employeeAmounts).forEach((employeeId) => {
      const current = employeeAmounts[employeeId] || 0;
      const original = originalAmounts[employeeId] || 0;
      const isModified = Math.abs(current - original) > 0.01;
      const hasNote = overrideNotes[employeeId]?.trim().length > 0;

      if (isModified && !hasNote) {
        const employee = eligibleEmployees.find((e) => e.id === employeeId);
        if (employee) {
          needingNotes.push(employee.name);
        }
      }
    });
    return needingNotes;
  };

  // Helper functions for calculations
  const getTotalDistributed = () => {
    return Object.values(employeeAmounts).reduce(
      (sum, amt) => sum + (amt || 0),
      0,
    );
  };

  const getRemainingBalance = () => {
    const remaining = totalTroncAmount - getTotalDistributed();
    // Round to 2 decimal places to avoid showing "-0.00" from floating point errors
    return Math.round(remaining * 100) / 100;
  };

  const getAverageAmount = () => {
    return eligibleEmployees.length > 0
      ? getTotalDistributed() / eligibleEmployees.length
      : 0;
  };

  // Employees with amounts for display
  const employeesWithAmounts = eligibleEmployees.map((emp) => ({
    ...emp,
    calculatedAmount: employeeAmounts[emp.id] || 0,
  }));

  // Check if ready to approve
  const canApprove =
    distributionValidation.isValid &&
    activeRule &&
    selectedDate &&
    !isProcessing &&
    Object.keys(employeeAmounts).length > 0 &&
    getEmployeesNeedingNotes().length === 0 &&
    !isGlobalAdmin;

  const handleApproveClick = () => {
    if (isGlobalAdmin) {
      showNotification(
        "Global Admins cannot approve distributions. Please use a Manager account.",
        "error",
      );
      return;
    }

    if (!canApprove) {
      if (Math.abs(getRemainingBalance()) >= 0.01) {
        showNotification(
          "Cannot approve: distribution total must equal TRONC amount",
          "error",
        );
      } else if (Object.keys(employeeAmounts).length === 0) {
        showNotification(
          "Cannot approve: no employees to distribute to",
          "error",
        );
      } else if (getEmployeesNeedingNotes().length > 0) {
        showNotification(
          `Cannot approve: notes required for modified amounts (${getEmployeesNeedingNotes().join(", ")})`,
          "error",
        );
      }
      return;
    }
    setShowApprovalModal(true);
  };

  const handleApproveConfirm = async () => {
    setShowApprovalModal(false);
    setIsProcessing(true);

    try {
      const troncRecordIds = selectedDateRecords.map((r) => r.id);
      // Round all amounts to 2 decimal places to avoid floating point issues
      const allocations = Object.entries(employeeAmounts).map(
        ([employeeId, amount]) => {
          const employee = eligibleEmployees.find((e) => e.id === employeeId);
          const original = originalAmounts[employeeId] || 0;
          const roundedAmount = Math.round(amount * 100) / 100;
          const roundedOriginal = Math.round(original * 100) / 100;
          const isModified = Math.abs(roundedAmount - roundedOriginal) > 0.01;
          const overrideNote = overrideNotes[employeeId];

          return {
            employeeId,
            amount: roundedAmount,
            percentage:
              totalTroncAmount > 0
                ? Math.round((roundedAmount / totalTroncAmount) * 10000) / 100
                : 0,
            jobRoleId: employee?.primaryJobRoleId || null,
            hoursWorked: employee?.hoursWorked || null,
            calculationMetadata: JSON.stringify({
              ruleName: activeRule.name,
              ruleType: activeRule.type,
              employeeName: employee?.name || "Unknown",
              roleName: employee?.role || "Unknown",
              isManualOverride: isModified,
              originalAmount: isModified ? roundedOriginal : undefined,
              overrideNote: isModified ? overrideNote : undefined,
            }),
          };
        },
      );

      const { distributionApi } = await import("../../services/api");
      const result = await distributionApi.approve({
        troncRecordIds,
        distributionRuleId: activeRule.id,
        allocations,
        notes: `Distribution using ${activeRule.name} rule for ${formatDate(selectedDate)}${Object.keys(overrideNotes).length > 0 ? " (includes manual overrides)" : ""}`,
      });

      if (result.success) {
        showNotification(
          `Successfully allocated ${formatCurrency(totalTroncAmount)} to ${allocations.length} employees.`,
          "success",
        );

        setEmployeeAmounts({});
        setOriginalAmounts({});
        setOverrideNotes({});
        setCalculatedDistribution([]);
        setSelectedDate(null);

        await refetchTroncRecords();
        await refetchRules();
      } else {
        showNotification(
          "Distribution approval failed: " +
            (result.message || "Unknown error"),
          "error",
        );
      }
    } catch (err) {
      console.error("Distribution approval error:", err);
      showNotification(
        "Failed to approve distribution: " + (err.message || "Unknown error"),
        "error",
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleExport = () => {
    if (isGlobalAdmin) {
      showNotification(
        "Global Admins cannot export distributions. Please use a Manager account.",
        "error",
      );
      return;
    }

    const headers = ["Employee", "Role", "Department", "Amount", "Percentage"];
    const rows = employeesWithAmounts.map((emp) => [
      emp.name,
      emp.role,
      emp.department || "N/A",
      formatCurrency(emp.calculatedAmount || 0),
      `${((emp.calculatedAmount / totalTroncAmount) * 100 || 0).toFixed(2)}%`,
    ]);
    const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tronc-distribution-${selectedDate || "export"}.csv`;
    a.click();
    showNotification("Distribution exported to CSV");
  };

  // Select Tenant Empty State for Global Admins
  if (isGlobalAdmin && needsTenantSelection) {
    return (
      <>
        <Helmet>
          <title>TRONC Distribution - Rocket Tronc</title>
        </Helmet>
        <PageLayout
          title="TRONC Distribution"
          description="Calculate and distribute TRONC among eligible employees."
          breadcrumbs={breadcrumbs}
        >
          <div className="card p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Icon name="Building2" size={40} className="text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-3">Select a Tenant</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              As a Global Administrator, please select a tenant from the sidebar
              to view and manage their TRONC distributions.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Icon name="ArrowLeft" size={16} />
              <span>Use the tenant selector in the sidebar</span>
            </div>
          </div>
        </PageLayout>
      </>
    );
  }

  // No Location Selected
  if (!currentLocation && hasLocations) {
    return (
      <>
        <Helmet>
          <title>TRONC Distribution - Rocket Tronc</title>
        </Helmet>
        <PageLayout
          title="TRONC Distribution"
          description="Calculate and distribute TRONC among eligible employees."
          breadcrumbs={breadcrumbs}
        >
          <div className="card p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Icon name="MapPin" size={40} className="text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-3">Select a Location</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Please select a location from the sidebar to view and distribute
              TRONC for that venue.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Icon name="ArrowLeft" size={16} />
              <span>Use the location selector in the sidebar</span>
            </div>
          </div>
        </PageLayout>
      </>
    );
  }

  const typeInfo = activeRule ? getDistributionTypeInfo(activeRule.type) : null;

  return (
    <>
      <Helmet>
        <title>
          TRONC Distribution -{" "}
          {currentLocation?.name || selectedTenant?.name || "Rocket Tronc"}
        </title>
        <meta
          name="description"
          content="Calculate and distribute TRONC among employees"
        />
      </Helmet>

      <PageLayout
        title="TRONC Distribution"
        description={
          currentLocation
            ? `${currentLocation.name} - Calculate and distribute TRONC among eligible employees.`
            : "Calculate and distribute TRONC among eligible employees using your configured rules."
        }
        breadcrumbs={breadcrumbs}
      >
        {/* Notification */}
        {notification && (
          <div
            className={`mb-6 alert ${notification.type === "error" ? "alert-error" : notification.type === "warning" ? "alert-warning" : "alert-success"}`}
          >
            <Icon
              name={
                notification.type === "error"
                  ? "AlertCircle"
                  : notification.type === "warning"
                    ? "AlertTriangle"
                    : "CheckCircle"
              }
              size={20}
            />
            <span>{notification.message}</span>
            <button onClick={() => setNotification(null)} className="ml-auto">
              <Icon name="X" size={16} />
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="card p-8 text-center mb-6">
            <div className="spinner mx-auto mb-4" />
            <p className="text-muted-foreground">
              Loading distribution data...
            </p>
          </div>
        )}

        {/* Date Selector */}
        {!loading && currentLocation && (
          <div className="mb-6 card p-4 border-2 border-primary/20 bg-primary/5">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon name="Calendar" size={24} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">
                    Select Date to Process
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {lastProcessedDate
                      ? `Last processed: ${formatDate(lastProcessedDate)}`
                      : "No distributions processed yet"}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap lg:flex-nowrap items-center gap-3">
                {availableDates.length > 0 ? (
                  <select
                    value={selectedDate || ""}
                    onChange={(e) => handleDateChange(e.target.value)}
                    className="input text-lg font-medium min-w-[220px]"
                  >
                    <option value="">Select a date...</option>
                    {availableDates.map((date) => (
                      <option key={date} value={date}>
                        {formatDate(date)}
                        {date === nextDateToProcess ? " (Next)" : ""}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="px-4 py-2 bg-muted rounded-lg text-muted-foreground">
                    No pending dates available
                  </div>
                )}

                {selectedDate && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-success/10 text-success rounded-lg">
                    <Icon name="PoundSterling" size={16} />
                    <span className="font-bold text-lg">
                      {formatCurrency(totalTroncAmount)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Active Rule with Strategy Breakdown */}
        {!loading && activeRule && (
          <div className="card mb-6 border-2 border-primary/20 overflow-hidden">
            {/* Rule Header */}
            <div className="p-6 flex-wrap gap-5 flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div
                  className={`w-14 h-14 rounded-xl ${typeInfo?.bgColor || "bg-primary/10"} flex items-center justify-center`}
                >
                  <Icon
                    name={typeInfo?.icon || "Settings"}
                    size={28}
                    className={typeInfo?.color || "text-primary"}
                  />
                </div>
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <h2 className="text-xl font-heading font-bold">
                      {activeRule.name}
                    </h2>
                    <span className="badge badge-success">Active</span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    <span className="font-medium">{typeInfo?.name}</span>
                    {activeRule.effectiveFrom && (
                      <span>
                        {" "}
                        • Active since{" "}
                        {new Date(
                          activeRule.effectiveFrom,
                        ).toLocaleDateString()}
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <Link
                to="/settings/distribution-rules"
                className="btn btn-outline btn-sm"
              >
                <Icon name="Settings" size={16} />
                Manage Rules
              </Link>
            </div>

            {/* Strategy Breakdown - Always Visible */}
            {strategyBreakdown && totalTroncAmount > 0 && (
              <div className="border-t border-border p-6 bg-muted/30">
                {/* Strategy Description */}
                <div className="mb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Icon
                      name="Calculator"
                      size={18}
                      className="text-primary"
                    />
                    <h3 className="font-semibold">Distribution Strategy</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {strategyBreakdown.description}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1 font-mono bg-muted px-2 py-1 rounded inline-block">
                    {strategyBreakdown.formula}
                  </p>
                </div>

                {/* Strategy Results */}
                {strategyBreakdown.type === "equal" ? (
                  <div className="bg-card rounded-lg border border-border p-6 text-center">
                    <div className="text-4xl font-bold text-primary mb-2">
                      {strategyBreakdown.result}
                    </div>
                    <p className="text-muted-foreground">per employee</p>
                    <p className="text-sm text-muted-foreground mt-2">
                      {formatCurrency(totalTroncAmount)} ÷{" "}
                      {eligibleEmployees.length} = {strategyBreakdown.result}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {/* Header */}
                    <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide px-3 pb-2">
                      <div className="col-span-4">
                        {strategyBreakdown.type === "byDepartment"
                          ? "Department"
                          : "Role"}
                      </div>
                      <div className="col-span-2 text-right">Share</div>
                      <div className="col-span-2 text-right">Amount</div>
                      <div className="col-span-2 text-right">Staff</div>
                      <div className="col-span-2 text-right">Per Person</div>
                    </div>

                    {/* Rows */}
                    {strategyBreakdown.items.map((item, index) => (
                      <div
                        key={index}
                        className="grid grid-cols-12 gap-2 items-center p-3 bg-card rounded-lg border border-border hover:border-primary/30 transition-colors"
                      >
                        <div className="col-span-4 flex items-center gap-2">
                          <div
                            className={`w-3 h-3 rounded-full`}
                            style={{
                              backgroundColor: `hsl(${(index * 60) % 360}, 70%, 50%)`,
                            }}
                          />
                          <span className="font-medium truncate">
                            {item.label}
                          </span>
                        </div>
                        <div className="col-span-2 text-right">
                          <span className="font-bold text-primary">
                            {item.percentage}%
                          </span>
                        </div>
                        <div className="col-span-2 text-right">
                          <span className="font-semibold">{item.value}</span>
                        </div>
                        <div className="col-span-2 text-right text-muted-foreground">
                          {item.count}
                        </div>
                        <div className="col-span-2 text-right">
                          <span className="font-medium text-success">
                            {item.perEmployee}
                          </span>
                        </div>
                      </div>
                    ))}

                    {/* Total */}
                    <div className="grid grid-cols-12 gap-2 items-center p-3 bg-primary/5 rounded-lg border-2 border-primary/20 mt-2">
                      <div className="col-span-4 font-bold">Total</div>
                      <div className="col-span-2 text-right font-bold">
                        100%
                      </div>
                      <div className="col-span-2 text-right font-bold">
                        {formatCurrency(totalTroncAmount)}
                      </div>
                      <div className="col-span-2 text-right text-muted-foreground">
                        {eligibleEmployees.length}
                      </div>
                      <div className="col-span-2 text-right font-bold text-success">
                        {formatCurrency(getAverageAmount())}
                        <span className="text-xs text-muted-foreground ml-1">
                          avg
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Additional info for weighted strategies */}
                {strategyBreakdown.type === "byWeightedHours" && (
                  <div className="mt-4 text-xs text-muted-foreground">
                    <span>
                      Total Weighted Points:{" "}
                      <strong>{strategyBreakdown.totalWeight}</strong> (hours
                      × role multiplier)
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* No date selected message */}
            {(!selectedDate || totalTroncAmount === 0) && (
              <div className="border-t border-border p-6 bg-muted/30 text-center">
                <Icon
                  name="Calendar"
                  size={24}
                  className="text-muted-foreground mx-auto mb-2"
                />
                <p className="text-sm text-muted-foreground">
                  Select a date above to see how this rule will distribute the
                  TRONC
                </p>
              </div>
            )}
          </div>
        )}

        {/* No Active Rule Warning */}
        {!loading && !activeRule && (
          <div className="card mb-6 p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-warning/10 flex items-center justify-center mx-auto mb-4">
              <Icon name="AlertTriangle" size={32} className="text-warning" />
            </div>
            <h3 className="text-lg font-semibold mb-2">
              No Active Distribution Rule
            </h3>
            <p className="text-muted-foreground mb-4 max-w-md mx-auto">
              A distribution rule must be active before you can distribute
              TRONC. Please activate a rule to continue.
            </p>
            <Link to="/settings/distribution-rules" className="btn btn-primary">
              <Icon name="Plus" size={18} />
              Configure Distribution Rules
            </Link>
          </div>
        )}

        {/* No data states */}
        {!loading && availableDates.length === 0 && (
          <div className="card p-8 text-center">
            <div className="empty-state-icon mx-auto mb-4">
              <Icon name="PoundSterling" size={32} />
            </div>
            <h3 className="text-lg font-semibold mb-2">
              No TRONC available for distribution
            </h3>
            <p className="text-muted-foreground max-w-md mx-auto">
              Record daily takings first to have TRONC available for
              distribution.
            </p>
          </div>
        )}

        {!loading &&
          selectedDate &&
          totalTroncAmount > 0 &&
          eligibleEmployees.length === 0 && (
            <div className="card p-8 text-center">
              <div className="empty-state-icon mx-auto mb-4">
                <Icon name="Users" size={32} />
              </div>
              <h3 className="text-lg font-semibold mb-2">
                No eligible employees
              </h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Add employees and mark them as eligible for TRONC to distribute
                funds.
              </p>
            </div>
          )}

        {/* Main content */}
        {!loading &&
          totalTroncAmount > 0 &&
          eligibleEmployees.length > 0 &&
          activeRule && (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Main Content - 3 columns */}
              <div className="lg:col-span-3 space-y-6">
                {/* Calculation Summary */}
                <CalculationSummary
                  totalAmount={totalTroncAmount}
                  recipientCount={eligibleEmployees.length}
                  averageAmount={getAverageAmount()}
                  remainingBalance={getRemainingBalance()}
                />

                {/* Employee Distribution */}
                <PageSection title="Employee Allocations">
                  <EmployeeDistributionList
                    employees={employeesWithAmounts}
                    onAmountChange={handleAmountChange}
                    onOverrideNotesChange={handleOverrideNotesChange}
                    isProcessing={isProcessing || previewLoading}
                    totalAmount={totalTroncAmount}
                    originalAmounts={originalAmounts}
                  />
                </PageSection>

                {/* Sign-off / Approval Section */}
                <div className="card p-6 border-2 border-border">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center">
                      <Icon
                        name="CheckCircle2"
                        size={24}
                        className="text-success"
                      />
                    </div>
                    <div>
                      <h2 className="text-xl font-heading font-semibold">
                        Sign-off & Approval
                      </h2>
                      <p className="text-sm text-muted-foreground">
                        Review and approve the distribution
                      </p>
                    </div>
                  </div>

                  {/* Approval Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="p-4 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">Date</p>
                      <p className="font-semibold">
                        {formatDate(selectedDate)}
                      </p>
                    </div>
                    <div className="p-4 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">
                        Total TRONC
                      </p>
                      <p className="font-semibold text-lg">
                        {formatCurrency(totalTroncAmount)}
                      </p>
                    </div>
                    <div className="p-4 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">
                        Recipients
                      </p>
                      <p className="font-semibold">
                        {eligibleEmployees.length} employees
                      </p>
                    </div>
                  </div>

                  {/* Validation Status */}
                  <div
                    className={`p-4 rounded-lg mb-6 ${canApprove ? "bg-success/10 border border-success/20" : "bg-warning/10 border border-warning/20"}`}
                  >
                    <div className="flex items-start gap-3">
                      <Icon
                        name={canApprove ? "CheckCircle2" : "AlertTriangle"}
                        size={20}
                        className={canApprove ? "text-success" : "text-warning"}
                      />
                      <div>
                        <p className="font-semibold mb-1">
                          {canApprove ? "Ready for Approval" : "Not Ready"}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {canApprove
                            ? "All TRONC has been allocated. You can now approve this distribution."
                            : getEmployeesNeedingNotes().length > 0
                              ? `Notes required for: ${getEmployeesNeedingNotes().join(", ")}`
                              : Math.abs(getRemainingBalance()) >= 0.01
                                ? `${formatCurrency(Math.abs(getRemainingBalance()))} ${getRemainingBalance() > 0 ? "remaining" : "over-allocated"}. Adjust amounts before approving.`
                                : "Select a date and ensure allocations are complete."}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    {isGlobalAdmin && (
                      <div className="col-span-2 p-3 bg-warning/10 border border-warning/20 rounded-lg text-center">
                        <p className="text-sm text-warning flex items-center justify-center gap-2">
                          <Icon name="Lock" size={16} />
                          Global Admins cannot approve or export distributions
                        </p>
                      </div>
                    )}
                    {!isGlobalAdmin && (
                      <>
                        <button
                          className="btn btn-outline flex-1"
                          onClick={handleExport}
                          disabled={isProcessing}
                        >
                          <Icon name="Download" size={18} />
                          Export to CSV
                        </button>
                        <button
                          className="btn btn-primary flex-1"
                          onClick={handleApproveClick}
                          disabled={!canApprove || isProcessing}
                        >
                          {isProcessing ? (
                            <>
                              <div className="spinner spinner-sm" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <Icon name="CheckCircle2" size={18} />
                              Approve & Allocate
                            </>
                          )}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Compliance Sidebar - Fixed position */}
              <div className="lg:col-span-1">
                <div className="sticky top-24">
                  <ComplianceSidebar
                    totalTronc={totalTroncAmount}
                    totalDistributed={getTotalDistributed()}
                    remainingBalance={getRemainingBalance()}
                    employeeCount={eligibleEmployees.length}
                    hasActiveRule={!!activeRule}
                    ruleName={activeRule?.name}
                  />
                </div>
              </div>
            </div>
          )}

        {/* Approval Confirmation Modal */}
        {showApprovalModal && (
          <div className="fixed inset-0 bg-black/50 z-[1100] flex items-center justify-center p-4">
            <div className="bg-card rounded-xl shadow-2xl max-w-md w-full p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Icon name="AlertCircle" size={32} className="text-primary" />
                </div>
                <h2 className="text-xl font-bold mb-2">
                  Confirm TRONC Allocation
                </h2>
                <p className="text-muted-foreground">
                  Are you sure you want to allocate{" "}
                  {formatCurrency(totalTroncAmount)} to{" "}
                  {eligibleEmployees.length} employees for{" "}
                  {formatDate(selectedDate)}?
                </p>
              </div>

              <div className="bg-muted rounded-lg p-4 mb-6 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Rule:</span>
                  <span className="font-medium">{activeRule?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Amount:</span>
                  <span className="font-medium">
                    {formatCurrency(totalTroncAmount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Employees:</span>
                  <span className="font-medium">
                    {eligibleEmployees.length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Average per employee:
                  </span>
                  <span className="font-medium">
                    {formatCurrency(getAverageAmount())}
                  </span>
                </div>
              </div>

              <p className="text-xs text-muted-foreground text-center mb-6">
                This action will create allocation records and mark the TRONC as
                distributed. This cannot be undone.
              </p>

              <div className="flex gap-3">
                <button
                  className="btn btn-outline flex-1"
                  onClick={() => setShowApprovalModal(false)}
                  disabled={isProcessing}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary flex-1"
                  onClick={handleApproveConfirm}
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <div className="spinner spinner-sm" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Icon name="Check" size={18} />
                      Confirm Allocation
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </PageLayout>
    </>
  );
};

export default TroncDistribution;
